import '@shared/ui/theme/global.css';
import './scope-tokens.css';

import { ApiError } from '@shared/api-client/index';
import { initI18n } from '@shared/ui/i18n/i18n.config';
import { useEvictCacheOnUserChange } from '@shared/ui/query/evict-on-user-change';
import { QueryProvider } from '@shared/ui/query/query-provider.component';
import { prePaintScript } from '@shared/ui/theme/theme';
import { ToastProvider } from '@shared/ui/toast/toast.component';
import type { ReactNode } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';

import en from './locales/en/common.json';
import { goToLogin } from './modules/core/auth';
import { QUERY_CACHE_KEY } from './modules/core/cache';
import { useMe } from './modules/core/session.hooks';
import { useScopeRealtime } from './modules/scopes/scope-realtime.hooks';

export { ErrorBoundary } from '@shared/ui/error/error-boundary.component';

// Bundle en so SSR + first client paint agree (no hydration mismatch).
initI18n({ en: { common: en } });

// No brand override: the shared dark theme's default --colour-brand-primary
// (#7c4dff) already matches the Scope mock, so this app takes the plain
// kiss.cat brand — same as Tasks and the auth shell.

export function Layout({ children }: { children: ReactNode }) {
	// suppressHydrationWarning: the pre-paint script sets data-theme on <html>
	// before React hydrates, so its attributes intentionally differ from SSR.
	return (
		<html lang="en" suppressHydrationWarning>
			<head>
				<meta charSet="utf-8" />
				<meta name="app-version" content={__APP_VERSION__} />
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1, interactive-widget=resizes-content"
				/>
				<meta name="robots" content="noindex, nofollow" />
				<link rel="icon" href="/favicon.svg" type="image/svg+xml" />
				{/* Resolve the theme before first paint to avoid a flash. */}
				<script dangerouslySetInnerHTML={{ __html: prePaintScript }} />
				<Meta />
				<Links />
			</head>
			<body>
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

export default function App() {
	return (
		<QueryProvider
			persistKey={QUERY_CACHE_KEY}
			buster={__BUILD_ID__}
			onError={(error) => {
				// A 401 from any query/mutation means the session lapsed mid-use — bounce to
				// the auth app (client-side only). useMe swallows its own 401 (returns null),
				// so this never loops on the session check. Every hook raises its own
				// toastApiError, so the global handler stays quiet otherwise (a global toast
				// would double up on each mutation's own onError).
				if (typeof window !== 'undefined' && error instanceof ApiError && error.status === 401) {
					goToLogin();
					return;
				}
				console.error(error);
			}}
		>
			<ToastProvider>
				<SessionCacheGuard />
				<ScopeRealtime />
				<Outlet />
			</ToastProvider>
		</QueryProvider>
	);
}

/** Live-reconcile scope broadcasts into the cache, app-wide (PLAN-46). Renders nothing;
 *  lives inside the QueryProvider so it can reach the shared query client. */
function ScopeRealtime() {
	useScopeRealtime();
	return null;
}

/** Drop a stale persisted cache when a different account becomes active (add-account /
 *  switch). Renders nothing; lives inside the QueryProvider to reach the query client. */
function SessionCacheGuard() {
	const { data: me } = useMe();
	useEvictCacheOnUserChange(me?.user.id, QUERY_CACHE_KEY);
	return null;
}
